<template>
    <div class="game__chat chat" :class="{'visible-mobile': isMobileChatVisible}" :style="{
        '--player-color-main': getPlayerColor(playerId),
        '--player-color-contrast': getPlayerColorContrast(playerId)
    }">
        <transition name="fade">
            <dice-generator v-show="isDiceVisible && playerId"
                            @send-dice-command="sendDiceRollCommand">
            </dice-generator>
        </transition>

        <div class="game__chat-messages chat__messages" v-custom-scroll
             ref="chat"
             @wheel="requestChatHistoryOnScroll">
            <div class="chat__margin"></div>
            <div class="chat-group" v-for="(group, groupIndex, key) in messages"
                 :key="key" :class="{'single': Object.keys(group.messages).length === 1, 'opened': group.opened}">
                <div class="game__chat-message chat__message" :data-index="index"
                     v-for="(message, index) in group.messages"
                     :style="{'color': getPlayerColor(message.playerId)}"
                     :class="{
                     'is-author': message.playerId === playerId,
                     'first': index === Object.keys(messages).shift(),
                     'last': index === Object.keys(messages).pop(),
                     'anonymous': message.anonymous === true,
                     'is-group': isGroupMessage(message, parseInt(index)),
                     'is-section-header': isSectionMessage(message),
                     'is-group-last': isLastGroupMessage(message, parseInt(index)),
                     'is-player-action': isPlayerAction(message),
                     'is-system-message': isSystemMessage(message),
                     'is-system-notification': isSystemNotification(message),
                     'is-voting': (isVotingStarted(message) || isVotingEnded(message)) && (message.data && !message.data.external),
                     'is-result': isResult(message),
                     'is-template': isTemplate(message)
                 }"
                     v-if="(group.opened || (!group.opened && isSectionMessage(message))) && isVisibleForPlayer(message)"
                >
                    <chat-voting v-if="showVoting(message) && (isVotingStarted(message) || isVotingEnded(message))"
                                 :message="message"
                                 :class="{'pinned': messageIsPinned(message), 'visibility-opened': showVisibilityControl}"
                                 @voting-status-changed="setMessageListPadding">
                    </chat-voting>

                    <chat-message-widget v-if="group.opened || (!group.opened && isSectionMessage(message))"
                                         :message="message"
                                         @toggle-section-visibility="toggleSectionVisibility">
                    </chat-message-widget>
                    <div class="chat__message-visibility"
                         v-if="message.playerId !== playerId && message.anonymous"
                    >
                        <button type="button" class="visibility"
                        >
                            <span class="visibility__anonymous">{{$t('message.anonymously')}}</span>
                        </button>
                    </div>

                    <div class="chat__message-visibility"
                         v-if="needShowMessageVisibilityControl(index) && !message.anonymous"
                         @click="toggleMessageControlFor(index)"
                         v-click-outside="closeMessageControlFor"
                    >
                        <div class="visibility"
                             :data-key="`${index}_${message.status}`"
                        >
                            <template v-for="(player, index) in players">
                                <div class="visibility-mark"
                                     v-if="player.id !== message.playerId"
                                     :key="index"
                                     :style="{'color': player.color.code}"
                                     :class="{'is-visible': message.visibleFor.length === 0 || message.visibleFor.indexOf(player.id) !== -1}"
                                ></div>
                            </template>
                            <template v-if="false" v-for="(player, index) in players">
                            <span class="visibility-name"
                                  v-if="player.id !== message.playerId"
                                  :key="index"
                                  :style="{'color': player.color.code}"
                                  :class="{'is-visible': message.visibleFor.length === 0 || message.visibleFor.indexOf(player.id) !== -1}"
                            >{{gameState.playersData[player.id].name}}</span>
                            </template>
                        </div>

                        <control-visibility
                            v-if="isMessageControlVisibleFor(index)"
                            class="control-visibility--player"
                            :player-id="playerId"
                            :players="players"
                            :is-message="true"
                            :visible-for="message.visibleFor"
                            @change-visibility="requestToggleMessageVisibility(message, $event)"
                            @change-visibility-all="requestToggleMessageVisibilityForAll(message)"
                        ></control-visibility>
                    </div>

                    <div class="chat__message-visibility"
                         v-if="needShowMessageAnonymousControl(message)"
                         @click="toggleMessageAnonymous(index)"
                    >
                        <button type="button" class="visibility"
                                v-if="!isMessageControlAnonymous(index)"
                                @click="showAnonymousMessage(message)"
                        >
                            <span class="visibility__anonymous">{{$t('message.anonymously')}}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <custom-buttons-container v-if="hasGameCustomButtons"></custom-buttons-container>

        <div class="game__chat-new-message chat__new-message">
            <button type="button" class="unread-messages-btn"
                    v-if="messagesToReadCounter"
                    @click="scrollToLastUnreadMessage">
                <span class="counter" :class="{'animate': showCounterAnimation}">
                    {{messagesToReadCounter}}
                    <span class="counter__animation">+1</span>
                </span>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 5L9 13.75L16 5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <div class="chat__new-message-text">
                <textarea :placeholder="getPlaceholder()"
                          v-model="message"
                          id="chatTextArea"
                          maxlength="1000"
                          :style="{'height': textareaHeight}"
                          @input="textareaAutosize"
                          @keydown.stop
                          @keyup.stop
                          @input.stop
                          @keydown.enter.exact.prevent
                          @keydown.enter.exact="sendMessageToChat"
                          @keydown.enter.shift.exact="newline"
                ></textarea>
                <button class="chat__new-message-send-btn"
                        type="button" :class="{'ready': message.length}"
                        @click="sendMessageToChat()">
                    <svg>
                        <use href="#ic_message-new"></use>
                    </svg>
                </button>
            </div>

            <control-visibility
                class="message-visibility-control"
                :class="{'visible': showVisibilityControl}"
                :player-id="playerId"
                :players="players"
                :visible-for="newMessageVisibleFor"
                @change-visibility="toggleNewMessageVisibilityFor"
                @change-visibility-all="toggleNewMessageVisibilityAll"
                @toggle-anonymity="toggleAnonymity"
                @toggle-list-visibility="toggleVisibilityControl"
            ></control-visibility>
        </div>
    </div>
</template>

<script>
    import store from "../store";
    import Tools from "../helpers/Tools";
    import DiceCommand from "../services/commands/DiceCommand";
    import PerfectScrollbar from "perfect-scrollbar";
    import EventApi from "../api/EventsApi";
    import MessageHelper from "../helpers/MessageHelper";
    import ChatMessagesTypes from "../../enums/chat-messages-types/ChatMessagesTypes";
    import ChatVoting from "./Chat/Messages/Voting/ChatVoting";
    import {ChatMessages} from "../../types/chat-messages";

    const MESSAGE_STATUS_PUBLIC = 1;
    const MESSAGE_STATUS_HIDDEN = 2;
    const CHAT_HISTORY_LIMIT = 50;

    const MESSAGE_TYPE_VOTE = 1;
    const MESSAGE_TYPE_PLAYER_ACTION = 2;
    const MESSAGE_TYPE_SYSTEM_MESSAGE = 3;
    const MESSAGE_TYPE_SYSTEM_NOTIFICATION = 4;
    const MESSAGE_TYPE_RESULT = 5;
    const MESSAGE_TYPE_CUSTOM_TEXT = 6;

    export default {
        components: {
            ChatVoting
        },
        props: {
            playerId: {
                required: true,
                type: String
            },
            isMobileChatVisible: {
                required: true,
                type: Boolean
            },
            isDiceVisible: {
                required: true,
                type: Boolean
            },
        },

        data() {
            return {
                messages: {},
                message: '',
                status: null,
                lastLoadedMessageId: null,
                CHAT_HISTORY_LIMIT: CHAT_HISTORY_LIMIT,
                showVisibilityControl: false,
                isMessageControlVisible: false,
                messageControlVisibleFor: [],
                messageControlAnonymous: [],
                newMessageVisibleFor: [],
                ignoreNewMessages: false,
                lastVisibleMessageId: null,
                isAnonymous: false,
                textareaHeight: null,
                messagePaddingBottom: '0',
                allowScrollToLastMessage: true,
                showCounterAnimation: false,
                messagesToReadCounter: 0,
            }
        },
        mounted() {
            EventApi.addEventListener(EventApi.REFRESH_SCROLL_CHAT, () => {
                this.$refs.chat.dispatchEvent(new CustomEvent('transitionend'));
            });
            EventApi.addEventListener(EventApi.REFRESH_CHAT, this.scrollToLastUnreadMessage)
            const chat = this.$refs.chat;
            chat.addEventListener('scroll', () => {
                this.allowScrollToLastMessage = chat.scrollTop + chat.clientHeight + 50 >= Math.max(chat.scrollHeight, chat.clientHeight);
                if (this.allowScrollToLastMessage) {
                    this.messagesToReadCounter = 0;
                }
            });
        },
        updated() {
            EventApi.triggerEvent(EventApi.REFRESH_SCROLL_CHAT);
        },
        computed: {
            players: () => store.getters.getPlayers,
            gameState: () => store.getters.getGameState,
            hasGameCustomButtons: function () {
                return this.gameState.zones && this.gameState.zones.chat.buttons.enabled;
            },
            /**
             * Текущая игра
             */
            currentActivity: () => store.getters.getCurrentActivity,

            /**
             * Настройки текущей игры
             */
            currentActivitySettings: function ()
            {
                if (!this.currentActivity) {
                    return null;
                }
                return this.currentActivity.settings;
            },
        },

        watch: {
            hasGameCustomButtons: function (newVal, oldVal)
            {
                if (newVal && this.allowScrollToLastMessage) {
                    this.scrollToLastMessage();
                }
            }
        },

        methods: {

            /**
             * Свернуть/развернуть секцию чата
             * @private
             */
            toggleSectionVisibility(id)
            {
                const group = this.messages[id];
                group.opened = !group.opened;
            },

            incrementMessagesToReadCounter()
            {
                this.messagesToReadCounter++;
                if (!this.showCounterAnimation) {
                    this.showCounterAnimation = true;
                    setTimeout(() => {
                        this.showCounterAnimation = false;
                    }, 700);
                }
            },

            resetMessagesToReadCounter()
            {
                this.messagesToReadCounter = 0;
            },

            /**
             * Скролл к последнему непрочитанному сообщению по клику на кнопку в чате
             */
            scrollToLastUnreadMessage()
            {
                this.allowScrollToLastMessage = true;
                this.scrollToLastMessage();
                this.resetMessagesToReadCounter();
            },

            /**
             * Установить отступ для списка сообщение
             */
            setMessageListPadding(height)
            {
                this.messagePaddingBottom = `${height}px`;

            },
            messageIsPinned(message)
            {
                return (parseInt(this.messagePaddingBottom) && !message.data.ended);
            },
            /**
             * Старт голосования
             */
            showVoting(message)
            {
                if (message.data && message.data.external) {
                    return false;
                }
                return message.data && message.data.type === MESSAGE_TYPE_VOTE;
            },
            /**
             * Показывать сообщение
             */
            showMessage(message)
            {
                if (message.data && message.data.external) {
                    return false;
                }
                return true;
            },
            /**
             * Старт голосования
             */
            isVotingStarted(message)
            {
                const date = Math.floor(Date.now() / 1000);
                return message.data
                && message.data.type === MESSAGE_TYPE_VOTE
                && message.command !== '/voteres'
                && !message.data.ended
                && (date < message.data.endTime || message.data.endTime === null);
            },
            /**
             * Конец голосования
             */
            isVotingEnded(message)
            {
                const date = Math.floor(Date.now() / 1000);
                if (message.data && message.data.ended && message.command === '/voteres') {
                    return message.data.ended;
                }

                return message.data
                    && message.data.type === MESSAGE_TYPE_VOTE
                    && message.command === '/voteres'
                    && (date >= message.data.endTime || message.data.endTime === null);
            },
            /**
             * Отправка введенного сообщения
             */
            sendMessageToChat()
            {
                this.allowScrollToLastMessage = true;
                this.status = MESSAGE_STATUS_PUBLIC;

                if (this.newMessageVisibleFor.length > 0) {
                    this.status = MESSAGE_STATUS_HIDDEN;
                }

                const data = {
                    text: this.message,
                    status: this.status,
                    visibleFor: this.newMessageVisibleFor,
                    anonymous: this.isAnonymous,
                };

                this.$parent.sendMessage('chatMessage', data);
                this.message = '';

                this.resetTextareaSize();
                this.scrollToLastMessage();
            },

            /**
             * Обработать реакцию
             */
            processReaction(data)
            {
                this.$refs.reactionButtons.processReaction(data);
            },

            /**
             * Отправить скрытое сообщение в чат
             */
            sendHiddenMessageToChat()
            {
                this.newMessageVisibleFor = [this.playerId];
                this.sendMessageToChat();
                this.newMessageVisibleFor = [];
            },

            /**
             * Новая строка
             */
            newline() {
                // по ходу это поведение по умолчанию при shift + enter
                //this.message = `${this.message}\n`;
            },

            /**
             * Цвет игрока по id
             * @param playerId
             * @return {null|*}
             */
            getPlayerColor(playerId)
            {
                return this.$parent.getPlayerColor(playerId);
            },

            getPlayerColorContrast(playerId)
            {
                return this.$parent.getPlayerColorContrast(playerId);
            },

            /**
             * Скролл к последнему сообщению
             */
            scrollToLastMessage()
            {
                Vue.nextTick(() => {
                    $('.game__chat-messages').each( function() {
                        this.scrollTop = Math.max(this.scrollHeight, this.clientHeight);
                    });
                });
            },

            /**
             * Обновить сообщение
             */
            updateMessage(messageData)
            {
                if (messageData.data && messageData.data.external) {
                    return false;
                }
                let messagesKeys = [];
                for (let message in this.messages) {
                    messagesKeys = [...messagesKeys, ...Object.keys(this.messages[message].messages)];
                }
                if (!messagesKeys.includes(messageData.message.id.toString())) {
                    return false;
                }
                let data = messageData.message;
                this.addMessageToGroup(data);

                return true;
            },

            /**
             * Запрос изменения видимости сообщения
             */
            requestToggleMessageVisibility(message, playerId) {
                const data = {
                    messageId: message.id,
                    players: [playerId]
                };

                MessageHelper.sendMessage('showHiddenMessage', data)
            },

            /**
             * Запрос изменения видимости сообщения видимо для всех
             */
            requestToggleMessageVisibilityForAll(message) {
                let players = [];

                if (message.status === MESSAGE_STATUS_HIDDEN) {
                    this.players.forEach((player) => {
                        players.push(player.id);
                    });
                }

                const data = {
                    messageId: message.id,
                    players: players
                };

                this.$parent.sendMessage('showHiddenMessage', data);
            },

            /**
             * Изменение видимости сообщения
             */
            toggleMessageVisibility(messageData) {
                const index = messageData.messageIndex;
                if (typeof this.messages[index] === 'undefined') {
                    return false;
                }
                const visibleFor = this.messages[index].visibleFor;
                this.messages[index].visibleFor = Tools.toggleArrayElement(visibleFor, messageData.playerId);

                return true;
            },

            addMessageToGroup(message)
            {
                let data = {
                    id: message.id,
                    data: message.data,
                    text: message.text,
                    playerId: message.playerId,
                    status: message.status,
                    visibleFor: message.visibleFor,
                    command: message.command,
                    forPlayers: message.forPlayers,
                    anonymous: message.anonymous,
                    isTemplate: message.template,
                    parentId: message.parentId
                };

                const groupId = data.parentId ?? data.id;

                if (typeof this.messages[groupId] !== 'undefined'
                    && typeof this.messages[groupId].messages[data.id] !== 'undefined') {
                    this.messages[groupId].messages[data.id] = data;
                    return;
                }

                if ((data.parentId && typeof this.messages[data.parentId] === 'undefined') || !data.parentId) {
                    Vue.set(this.messages, groupId, {
                        opened: true,
                        messages: {}
                    });
                    Vue.set(this.messages[groupId].messages, data.id, data);
                } else if(data.parentId && typeof this.messages[data.parentId] !== 'undefined') {
                    Vue.set(this.messages[groupId].messages, data.id, data);
                }
            },

            /**
             * Добавить сообщение в чат
             */
            addMessageToChat(message)
            {
                if (this.ignoreNewMessages) {
                    return;
                }

                this.addMessageToGroup(message);

                if (this.allowScrollToLastMessage) {
                    this.scrollToLastMessage();
                    this.resetMessagesToReadCounter();
                } else {
                    this.incrementMessagesToReadCounter();
                }
                this.afterNewMessage(message);
            },

            /**
             * После добавления нового сообщения
             */
            afterNewMessage(message)
            {
                if (!this.isMessageVisible(message)) {
                    return;
                }

                const command = message.command;
                if (!command) {
                    return;
                }

                // нужно показать анимацию броска кубиков
                if (DiceCommand.isValidCommand(command)) {
                    const commandBody = DiceCommand.getCommandBody(command);
                    const messageDiceSizes = DiceCommand.getDiceSizes(commandBody);
                    const values = message.text.split(' ');
                    if (values.length > 0) {
                        let valuesWithTypes = [];
                        let resultIndex = 0;
                        values.forEach((item) => {
                            let type = typeof messageDiceSizes[resultIndex] !== 'undefined' ? messageDiceSizes[resultIndex] : null;
                            resultIndex++;

                            if (type) {
                                valuesWithTypes.push({
                                    value: parseInt(item),
                                    type: 'd' + type,
                                });
                            }
                        });

                        if (valuesWithTypes.length) {
                            const realValues = DiceCommand.transformValuesForRealDices(valuesWithTypes);
                            EventApi.triggerEvent(EventApi.EVENT_START_DICES_ANIMATION, {
                                values: realValues,
                                color: message.anonymous ? '#bdbdbd' : this.getPlayerColor(message.playerId)
                            });
                        }
                    }

                }
            },

            /**
             * Добавить историю сообщений в чат
             * @param data
             */
            addChatHistory(data)
            {
                this.ignoreNewMessages = false;
                this.lastVisibleMessageId = data.lastVisibleMessageId;
                const chatMessages = data.chatMessages;

                Object.values(chatMessages).forEach(message => this.addMessageToGroup(message));

                if (typeof Object.keys(chatMessages)[0] !== 'undefined') {
                    this.lastLoadedMessageId = Object.keys(chatMessages)[0];
                }

                if (this.allowScrollToLastMessage) {
                    this.scrollToLastMessage();
                }
            },

            /**
             * Удалить историю чата
             */
            cleanChatHistory()
            {
                this.messages = {};
                this.lastLoadedMessageId = null;
                // игнорируем новые сообщения пока не придет свежая история
                this.ignoreNewMessages = true;

                const chat = this.$refs.chat;
                if (chat.hasOwnProperty('perfectScrollbar')) {
                    chat.scrollTop = 0;
                    chat.perfectScrollbar.destroy();
                    setTimeout(() => {
                        chat.perfectScrollbar = new PerfectScrollbar(chat, {
                            minScrollbarLength: 40
                        });
                    },100);
                }
            },

            /**
             * Запрос истории чата при переполнении чата по скроллу
             */
            requestChatHistoryOnScroll()
            {
                const chat = this.$refs.chat;
                if ((Number(this.lastLoadedMessageId) - 1) > Number(this.lastVisibleMessageId) && !chat.scrollTop) {
                    const scrollHeightBefore = chat.scrollHeight;
                    let scrollHeightAfter = 0;
                    this.requestChatHistoryMessage();
                    setTimeout(() => {
                        scrollHeightAfter = chat.scrollHeight;
                        chat.scrollTop = scrollHeightAfter - scrollHeightBefore;
                    }, 100);

                }
            },

            /**
             * Запрос истории чата
             */
            requestChatHistoryMessage()
            {
                const data = {
                    limit: CHAT_HISTORY_LIMIT,
                    from: this.lastLoadedMessageId
                };

                this.$parent.sendMessage('requestChatHistory', data);
            },

            /**
             * Переключить отображение кнопок управления видимостью для нового сообщения
             */
            toggleVisibilityControl() {
                this.showVisibilityControl = !this.showVisibilityControl;
                // if (this.showVisibilityControl) {
                //     this.newMessageVisibleFor = [this.playerId];
                // } else {
                //     this.newMessageVisibleFor = [];
                // }
            },

            /**
             * Переключить отображение кнопок управления видимостью для сообщения в чате
             * @param messageId - ID сообщения в чате
             */
            toggleMessageControlFor(messageId) {
                this.messageControlVisibleFor = Tools.toggleArrayElement(this.messageControlVisibleFor, messageId);
            },

            closeMessageControlFor() {
                this.messageControlVisibleFor = [];
            },

            /**
             * Получить статус отображения кнопок управления видимостью для сообщения в чате
             * @param messageId - ID сообщения в чате
             * @returns {boolean}
             */
            isMessageControlVisibleFor(messageId) {
                return this.messageControlVisibleFor.indexOf(messageId) !== -1;
            },

            /**
             * Переключить отображение кнопок управления анонимностью для сообщения в чате
             * @param messageId - ID сообщения в чате
             */
            toggleMessageAnonymous(messageId) {
                this.messageControlAnonymous = Tools.toggleArrayElement(this.messageControlAnonymous, messageId);
            },

            /**
             * Получить статус отображения кнопок управления анонимностью для сообщения в чате
             * @param messageId - ID сообщения в чате
             * @returns {boolean}
             */
            isMessageControlAnonymous(messageId) {
                return this.messageControlAnonymous.indexOf(messageId) !== -1;
            },

            /**
             * Изменить видимость нового сообщения для конкретных ситов
             * @param playerId - ID игрока
             */
            toggleNewMessageVisibilityFor(playerId) {
                this.newMessageVisibleFor = Tools.toggleArrayElement(this.newMessageVisibleFor, playerId);
            },

            /**
             * Изменить видимость нового сообщения для всех ситов
             */
            toggleNewMessageVisibilityAll() {

                if (this.newMessageVisibleFor.length > 1) {
                    // Если видимо для кого-то кроме автора - выключаем видимость для всех, кроме автора
                    this.newMessageVisibleFor = [this.playerId];
                } else {
                    // Если видимо только автору - включаем видимость для всех
                    const players = this.players;
                    this.newMessageVisibleFor = players.map((el) => {
                        return el.id;
                    });
                }
            },

            /**
             * Собощение видно текущему игроку
             */
            isMessageVisible(message) {
                if (!message) {
                    return false;
                }
                if (message.playerId === this.playerId) {
                    return true;
                }

                return message.status === MESSAGE_STATUS_PUBLIC || message.visibleFor.indexOf(this.playerId) !== -1;
            },

            /**
             * Нужно показать контроль видимости сообщения
             * @param index
             * @returns {boolean}
             */
            needShowMessageVisibilityControl(index)
            {
                const message = this.messages[index];
                if (!message || message.playerId !== this.playerId) {
                    return false;
                }

                return message.status === MESSAGE_STATUS_HIDDEN;
            },

            /**
             * Нужно показать контроль анонимности сообщения
             * @returns {boolean}
             */
            needShowMessageAnonymousControl(message)
            {
                if (message.playerId !== this.playerId) {
                    return false;
                }

                return message.anonymous === true;
            },

            /**
             * Подстройка высоты поля ввода под контент
             * @param e
             */
            textareaAutosize(e) {
                const textarea = e.target;
                this.textareaHeight = null;
                this.textareaHeight = `${textarea.scrollHeight}px`;
            },

            /**
             * Сбросить высоту текстового поля чата
             */
            resetTextareaSize() {
                this.textareaHeight = null;
            },

            /**
             * Отправить команду с броском дайса
             * @param command
             */
            sendDiceRollCommand()
            {
                this.allowScrollToLastMessage = true;
                this.scrollToLastMessage();
            },

            /**
             * Тип команды
             * @param command
             */
            getMessageCommandType(command)
            {

                if (DiceCommand.isValidCommand(command)) {
                    return 'dice';
                }

                return null;
            },

            /**
             * Сообщение видимо для игрока
             * @param message
             * @return {boolean}
             */
            isVisibleForPlayer(message)
            {
                if (typeof message.forPlayers === 'undefined' || !message.forPlayers.length) {
                    return true;
                }

                return message.forPlayers.indexOf(this.playerId) !== -1;
            },

            /**
             * Изменить анонимность сообщения
             * @param isAnonymous
             */
            toggleAnonymity(isAnonymous)
            {
                this.isAnonymous = isAnonymous;
            },

            /**
             * Деанон сообщения
             */
            showAnonymousMessage(message)
            {
                MessageHelper.sendMessage('showAnonymousMessage', {
                    messageId: message.id,
                });
            },

            /**
             * Очистить id последнего загруженного сообщения
             * (необходимо для запроса полной истории чата при переподключении)
             */
            clearLastLoadedMessageId()
            {
                this.lastLoadedMessageId = null;
            },

            /**
             * Максимум из массива
             * @param numArray
             */
            getMaxOfArray(numArray) {
                return Math.max.apply(null, numArray);
            },

            /**
             * Отправка сообщения
             * @param type
             * @param data
             */
            sendMessage(type, data)
            {
                this.$parent.sendMessage(type, data);
            },

            /**
             * Флаг для сообщений, отправленных пользователями
             * @return {boolean}
             */
            isPlayerMessage(message) {
                return !!message.playerId;
            },

            /**
             * Флаг для идущих подряд пользовательских сообщений
             * @param message
             * @param index
             * @return {boolean}
             */
            isGroupMessage(message, index) {
                return message.playerId
                    && this.messages[index - 1]
                    && this.messages[index - 1].playerId === message.playerId;
            },

            isLastGroupMessage(message, index) {
                return message.playerId &&
                    this.isGroupMessage(message, index) && (
                        !this.messages[index + 1] ||
                        !this.messages[index + 1].playerId ||
                        this.messages[index + 1].playerId !== message.playerId
                    );
            },

            /**
             * Заголовок
             * В заголовок помещается название Раунда
             * @param message
             * @private
             */
            isSectionMessage(message)
            {
                return message.data && message.data.type === ChatMessagesTypes.MESSAGE_TYPE_SECTION_MESSAGE;
            },

            isPlayerAction(message) {
                return message.data && message.data.type === ChatMessagesTypes.MESSAGE_TYPE_PLAYER_ACTION;
            },

            isSystemMessage(message) {
                return message.data && message.data.type === ChatMessagesTypes.MESSAGE_TYPE_SYSTEM_MESSAGE;
            },

            isSystemNotification(message) {
                return message.data && message.data.type === MESSAGE_TYPE_SYSTEM_NOTIFICATION
                    || message.data && message.data.type === MESSAGE_TYPE_CUSTOM_TEXT;
            },

            isResult(message) {
                return message.data && message.data.type === MESSAGE_TYPE_RESULT;
            },

            isTemplate(message) {
                return message.template
            },

            /**
             * Плейсхолдер поля ввода
             */
            getPlaceholder()
            {
                if (this.gameState.zones && this.gameState.zones.chat.disabledFor && this.gameState.zones.chat.disabledFor.indexOf(this.playerId) !== -1) {
                    return this.gameState.zones.chat.disabledPlaceholder;
                }

                return this.$t('message.type_smth')
            }
        }
    }
</script>

<style scoped>
    .show-more-button {
        margin-bottom: 10px;
    }
    .fade-enter-active, .fade-leave-active {
        opacity: 1;
        transition: opacity 0.3s ease;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0;
        transition: opacity 0.3s ease;
    }
</style>
